import { Link } from 'react-router-dom'
import { Card } from '@/components/common/UI/Card'
import { Button } from '@/components/common/UI/Button'

const candidateSteps = [
  {
    number: '01',
    title: 'Создайте аккаунт соискателя',
    text: 'При регистрации выберите роль «Соискатель». После входа откройте резюме и добавьте должность, навыки, опыт и желаемые условия.',
    link: { to: '/register', label: 'Зарегистрироваться' },
  },
  {
    number: '02',
    title: 'Найдите подходящие вакансии',
    text: 'Введите профессию или ключевые навыки. В списке вакансий уточните город, зарплату, график и другие условия.',
    link: { to: '/vacancies', label: 'Перейти к вакансиям' },
  },
  {
    number: '03',
    title: 'Откликнитесь на вакансию',
    text: 'Откройте описание, выберите резюме и при желании добавьте сопроводительное письмо. Перед отправкой проверьте, что данные резюме актуальны.',
    link: { to: '/applications', label: 'Мои отклики' },
  },
  {
    number: '04',
    title: 'Следите за ответом',
    text: 'Статус каждого отклика доступен в личном кабинете. Если работодатель пригласит вас на интервью или назначит тестовое задание, оно появится там же.',
    link: { to: '/notifications', label: 'Уведомления' },
  },
]

const employerSteps = [
  {
    number: '01',
    title: 'Создайте аккаунт работодателя и компанию',
    text: 'Выберите роль «Работодатель» при регистрации. Заполните профиль компании: кандидаты увидят описание и опубликованные вакансии.',
    link: { to: '/register', label: 'Создать аккаунт' },
  },
  {
    number: '02',
    title: 'Опубликуйте вакансию',
    text: 'Укажите обязанности, требования, зарплату и формат работы. После отправки дождитесь модерации — затем вакансия появится в поиске.',
    link: { to: '/vacancies/create', label: 'Разместить вакансию' },
  },
  {
    number: '03',
    title: 'Разберите отклики',
    text: 'Откройте отклик, изучите резюме и меняйте этап кандидата на канбан-доске. При необходимости назначьте интервью или тестовое задание.',
    link: { to: '/dashboard', label: 'К кабинету работодателя' },
  },
  {
    number: '04',
    title: 'Работайте вместе с командой',
    text: 'В профиле компании можно добавить коллегу по email. Коллега должен заранее зарегистрироваться на сайте с ролью работодателя.',
    link: { to: '/companies', label: 'Профили компаний' },
  },
]

function StepList({ title, description, steps }: {
  title: string
  description: string
  steps: typeof candidateSteps
}) {
  return (
    <section className="mt-10" aria-labelledby={title.replace(/\s+/g, '-')}
    >
      <div className="max-w-2xl">
        <h2 id={title.replace(/\s+/g, '-')} className="text-2xl font-bold text-secondary-900">{title}</h2>
        <p className="mt-2 text-secondary-600">{description}</p>
      </div>
      <ol className="mt-5 grid gap-4 md:grid-cols-2">
        {steps.map((step) => (
          <li key={step.number}>
            <Card className="flex h-full flex-col p-5">
              <span className="text-sm font-bold text-primary-700">ШАГ {step.number}</span>
              <h3 className="mt-2 text-lg font-semibold text-secondary-900">{step.title}</h3>
              <p className="mt-2 flex-1 text-sm leading-6 text-secondary-600">{step.text}</p>
              <Link to={step.link.to} className="mt-4 self-start text-sm font-medium text-primary-700 hover:underline">
                {step.link.label} →
              </Link>
            </Card>
          </li>
        ))}
      </ol>
    </section>
  )
}

export default function Help() {
  return (
    <main className="container-custom mx-auto max-w-5xl px-4 py-10 pb-16">
      <div className="rounded-2xl bg-gradient-to-br from-primary-50 via-white to-blue-50 px-6 py-9 sm:px-10">
        <p className="text-sm font-semibold uppercase tracking-wide text-primary-700">LenJob · Руководство</p>
        <h1 className="mt-2 text-3xl font-bold text-secondary-900 sm:text-4xl">Как пользоваться сайтом</h1>
        <p className="mt-3 max-w-2xl leading-7 text-secondary-600">
          Краткие инструкции помогут пройти путь от регистрации до отклика или найма. Выберите свой сценарий и переходите к нужному разделу.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={() => document.getElementById('candidate-guide')?.scrollIntoView({ behavior: 'smooth' })}>Я ищу работу</Button>
          <Button variant="outline" onClick={() => document.getElementById('employer-guide')?.scrollIntoView({ behavior: 'smooth' })}>Я нанимаю</Button>
        </div>
      </div>

      <div id="candidate-guide">
        <StepList
          title="Соискателю"
          description="Заполните резюме, откликайтесь на вакансии и проверяйте статусы в одном кабинете."
          steps={candidateSteps}
        />
      </div>

      <div id="employer-guide">
        <StepList
          title="Работодателю"
          description="Публикуйте вакансии и ведите кандидатов по этапам найма."
          steps={employerSteps}
        />
      </div>

      <section className="mt-10 rounded-xl border border-secondary-200 bg-secondary-50 p-6 sm:p-8">
        <h2 className="text-xl font-semibold text-secondary-900">Полезные инструменты</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Link to="/salary-calculator" className="rounded-lg bg-white p-4 text-sm font-medium text-primary-700 shadow-sm hover:shadow">
            Калькулятор зарплат <span aria-hidden="true">→</span>
            <span className="mt-1 block font-normal text-secondary-600">Сравните зарплаты по профессии и городу.</span>
          </Link>
          <Link to="/faq" className="rounded-lg bg-white p-4 text-sm font-medium text-primary-700 shadow-sm hover:shadow">
            Частые вопросы <span aria-hidden="true">→</span>
            <span className="mt-1 block font-normal text-secondary-600">Ответы о регистрации, откликах и приватности.</span>
          </Link>
        </div>
      </section>
    </main>
  )
}
